import React from 'react';
import { Card } from '~/src/components/Card';
import MiniSpinner from '~/src/components/Spinner/MiniSpinner';
import { InlineNotification } from '~/src/components/Notification/InlineNotification';
import { useCbsSubscriptionDetails } from '~/src/hooks/cbs/useCbsSubscriptionDetails';

interface CurrentPlanOverviewProps {
  subscription: any;
  pendingRequest?: boolean;
}

export const CurrentPlanOverview: React.FC<CurrentPlanOverviewProps> = ({
  subscription,
  pendingRequest = false,
}) => {
  const { data: subscriptionDetails } = useCbsSubscriptionDetails();

  if (!subscription) {
    return null;
  }

  const planName = subscription?.plan?.name || 'Current Plan';
  const billingCycle = subscription?.plan?.interval || 'Unknown';
  const licenseQuantity = subscription?.paidQuantity || 1;
  const courtFormsLibraries = subscription?.contentPackagesPurchased || 0;
  const status = subscription?.status || 'active';
  const nextBillingDate = subscription?.current_period_end;
  const hasReseller = subscriptionDetails?.has_reseller;
  const hasPendingChanges =
    subscriptionDetails?.has_pending_billing_cycle_change ||
    subscriptionDetails?.has_pending_license_quantity_change;

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  const getStatusIndicator = () => {
    if (status === 'active') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 ml-2">
          Active
        </span>
      );
    } else if (status === 'canceled') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 ml-2">
          Canceled
        </span>
      );
    } else if (status === 'past_due') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 ml-2">
          Past Due
        </span>
      );
    }
    return null;
  };

  return (
    <Card>
      <div className="space-y-4">
        <div className="space-y-3">
          <div>
            <dt className="text-sm font-medium text-gray-500">Plan</dt>
            <dd className="text-lg font-semibold text-gray-900 flex items-center">
              {planName}
              {getStatusIndicator()}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Billing Cycle</dt>
            <dd className="text-sm text-gray-900">{billingCycle}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">
              License Quantity
            </dt>
            <dd className="text-sm text-gray-900">
              {licenseQuantity} {licenseQuantity === 1 ? 'license' : 'licenses'}
            </dd>
          </div>
          {courtFormsLibraries > 0 && (
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Court Forms Libraries
              </dt>
              <dd className="text-sm text-gray-900">
                {courtFormsLibraries}{' '}
                {courtFormsLibraries === 1 ? 'library' : 'libraries'}
              </dd>
            </div>
          )}
          {nextBillingDate && (
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Next Billing Date
              </dt>
              <dd className="text-sm text-gray-900">
                {formatDate(nextBillingDate)}
              </dd>
            </div>
          )}
          {hasReseller && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Billing</dt>
              <dd className="text-sm text-gray-900">Managed by reseller</dd>
            </div>
          )}
        </div>

        {hasPendingChanges && !hasReseller && (
          <InlineNotification
            type="warning"
            title="Pending changes"
            message="Your subscription has scheduled updates"
          />
        )}

        {/* Loading overlay for pending requests */}
        {pendingRequest && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <MiniSpinner className="text-blue-500" />
              <span className="text-sm text-gray-600">
                Updating subscription...
              </span>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
